/**
 * Centralized query registry + global authed queryFn.
 *
 * How it works
 * ------------
 * Every queryKey on the site has the shape:
 *
 *   [registryKey, ...args]
 *
 * `registryKey` is a string that maps to a TanStack Start server function in
 * the `QUERY_REGISTRY` below. The QueryClient's default `queryFn` looks up
 * the server fn from the registry, attaches the user's Supabase access token
 * (when the entry is marked `auth: true`), and calls it with the validated
 * input arg.
 *
 * Benefits
 * --------
 * - One place to wire auth headers, no more `await authedHeaders()` scattered
 *   across every admin component.
 * - One place that translates a queryKey into a network call, uniform
 *   loading / error / retry behavior comes "for free" from TanStack Query.
 * - Public reads use the same caching layer (no auth header, just a server fn
 *   lookup) so the SSR loader → client hydration handoff is consistent.
 *
 * Adding a new query
 * ------------------
 * 1. Import the server fn here.
 * 2. Add an entry to `QUERY_REGISTRY` with `{ fn, auth }`.
 * 3. Export a `queryOptions(...)` factory below, components and route
 *    loaders should always use the factory, never construct queryKeys by
 *    hand. This keeps the queryKey ↔ argument shape locked together.
 *
 * Mutations stay outside this registry. Use `useAuthedMutation` (see
 * src/lib/authed-mutation.ts) which handles the same auth-header injection
 * for write operations.
 */

import { queryOptions, type QueryClient, type QueryFunction } from "@tanstack/react-query";

import {
  listPublicProjects,
  listAdminProjects,
  getProject,
  checkIsAdmin,
} from "./projects.functions";
import {
  listPublicResources,
  getPublicResource,
  getPublicResourcePage,
  getRelatedResources,
  listAdminResources,
  getResource,
} from "./resources.functions";
import {
  listPublicBlogPosts,
  listPublicBlogPostsByCategory,
  listPublicBlogCategoryCounts,
  getPublicBlogPostPage,
  listAdminBlogPosts,
  getBlogPost,
} from "./blog.functions";
import { listTeamMembers } from "./team.functions";
import { getQuoteById } from "./quotes.functions";
import { getSiteSettings } from "./site-settings.functions";
import {
  listPublicEcosystemPartners,
  listAdminEcosystemPartners,
} from "./ecosystem-partners.functions";
import { listAuditLeads } from "./leads.functions";
import {
  listMyOrders,
  listAllOrders,
  getAdminOrder,
  getAdminAuditSections,
  listMyOrderAuditSections,
  getAdminReportContent,
  getOrderQueueStatus,
} from "./orders.functions";
import { getAiSettings } from "./ai-settings.functions";
import { getReportSettings, listReportAccessLog } from "./report-settings.functions";

// -----------------------------------------------------------------------------
// Registry
// -----------------------------------------------------------------------------

type ServerFn = (opts: { data?: unknown; headers?: Record<string, string> }) => Promise<unknown>;

interface RegistryEntry {
  fn: ServerFn;
  /** When true, the global queryFn attaches the current user's bearer token. */
  auth: boolean;
}

const QUERY_REGISTRY = {
  // Public reads
  "public.projects.list": { fn: listPublicProjects as ServerFn, auth: false },
  "public.resources.list": { fn: listPublicResources as ServerFn, auth: false },
  "public.resource.get": { fn: getPublicResource as ServerFn, auth: false },
  "public.resource.page": { fn: getPublicResourcePage as ServerFn, auth: false },
  "public.resources.related": { fn: getRelatedResources as ServerFn, auth: false },
  "public.blog.list": { fn: listPublicBlogPosts as ServerFn, auth: false },
  "public.blog.by-category": { fn: listPublicBlogPostsByCategory as ServerFn, auth: false },
  "public.blog.category-counts": { fn: listPublicBlogCategoryCounts as ServerFn, auth: false },
  "public.blog.page": { fn: getPublicBlogPostPage as ServerFn, auth: false },
  "public.quote.get": { fn: getQuoteById as ServerFn, auth: false },
  "public.site-settings.get": { fn: getSiteSettings as ServerFn, auth: false },
  "public.ecosystem-partners.list": { fn: listPublicEcosystemPartners as ServerFn, auth: false },

  // Admin reads (auth required)
  "admin.projects.list": { fn: listAdminProjects as ServerFn, auth: true },
  "admin.project.get": { fn: getProject as ServerFn, auth: true },
  "admin.resources.list": { fn: listAdminResources as ServerFn, auth: true },
  "admin.ecosystem-partners.list": { fn: listAdminEcosystemPartners as ServerFn, auth: true },
  "admin.resource.get": { fn: getResource as ServerFn, auth: true },
  "admin.blog.list": { fn: listAdminBlogPosts as ServerFn, auth: true },
  "admin.blog.get": { fn: getBlogPost as ServerFn, auth: true },
  "admin.team.list": { fn: listTeamMembers as ServerFn, auth: true },
  "admin.auth.check": { fn: checkIsAdmin as ServerFn, auth: true },
  "admin.leads.list": { fn: listAuditLeads as ServerFn, auth: true },
  "portal.orders.list": { fn: listMyOrders as ServerFn, auth: true },
  "admin.orders.list": { fn: listAllOrders as ServerFn, auth: true },
  "admin.order.get": { fn: getAdminOrder as ServerFn, auth: true },
  "admin.audit-sections.list": { fn: getAdminAuditSections as ServerFn, auth: true },
  "admin.report-content.get": { fn: getAdminReportContent as ServerFn, auth: true },
  "admin.order.queue-status": { fn: getOrderQueueStatus as ServerFn, auth: true },
  "portal.audit-sections.list": { fn: listMyOrderAuditSections as ServerFn, auth: true },
  "admin.ai-settings.get": { fn: getAiSettings as ServerFn, auth: true },
  "admin.report-settings.get": { fn: getReportSettings as ServerFn, auth: true },
  "admin.report-access-log.list": { fn: listReportAccessLog as ServerFn, auth: true },
} as const satisfies Record<string, RegistryEntry>;

export type RegistryKey = keyof typeof QUERY_REGISTRY;

// -----------------------------------------------------------------------------
// Global queryFn
// -----------------------------------------------------------------------------

async function authHeaders(): Promise<Record<string, string>> {
  // Lazy-load the Supabase client so public pages never ship @supabase/auth-js.
  // Only admin-marked queries (auth: true) hit this path.
  const { supabase } = await import("@/integrations/supabase/client");
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Default queryFn used by every `useQuery` / `ensureQueryData` call.
 *
 * The queryKey's first element MUST be a `RegistryKey`. The second element
 * (if present) is the server fn's `data` argument.
 */
export const globalQueryFn: QueryFunction<unknown, readonly unknown[]> = async ({
  queryKey,
  signal: _signal,
}) => {
  const [key, arg] = queryKey as [RegistryKey, unknown?];
  const entry = QUERY_REGISTRY[key];
  if (!entry) {
    throw new Error(`Unknown query key: ${String(key)}`);
  }
  const headers = entry.auth ? await authHeaders() : undefined;
  try {
    return await entry.fn({
      ...(arg !== undefined ? { data: arg } : {}),
      ...(headers ? { headers } : {}),
    });
  } catch (err) {
    // requireSupabaseAuth (and other server middleware) throws a raw `Response`
    // on 401/403. React Query then stores it as the `error`, but `Response`
    // has no `.message`, which breaks downstream `error.message` checks and
    // surfaces as an unhandled "[object Response]" rejection. Normalize to a
    // real Error here so callers can branch on the status.
    if (err instanceof Response) {
      const text = await err
        .clone()
        .text()
        .catch(() => "");
      const e = new Error(
        err.status === 401 || err.status === 403
          ? `Unauthorized: ${text || err.statusText || err.status}`
          : `Request failed (${err.status}): ${text || err.statusText}`,
      );
      (e as Error & { status?: number }).status = err.status;
      throw e;
    }
    throw err;
  }
};

// -----------------------------------------------------------------------------
// queryOptions factories, always use these from components/loaders.
// -----------------------------------------------------------------------------

// Public
export const publicProjectsQuery = () =>
  queryOptions({
    queryKey: ["public.projects.list"] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof listPublicProjects>>>,
  });

export const publicResourcesQuery = () =>
  queryOptions({
    queryKey: ["public.resources.list"] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof listPublicResources>>>,
  });

/**
 * Hub pages must remain renderable when the public-resources RPC or client
 * hydration fails transiently. The editorial pillar and recommended paths are
 * static; only the supplemental card grid depends on this query.
 */
export async function loadPublicResourcesForHub(queryClient: QueryClient) {
  try {
    return await queryClient.ensureQueryData(publicResourcesQuery());
  } catch (error) {
    console.error("Public resource hub query failed; rendering editorial fallback", error);
    queryClient.removeQueries({ queryKey: ["public.resources.list"], exact: true });
    return { resources: [] as any[], taxonomyAvailable: false };
  }
}

export const publicResourceQuery = (slug: string) =>
  queryOptions({
    queryKey: ["public.resource.get", { slug }] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof getPublicResource>>>,
  });

export const publicResourcePageQuery = (input: {
  slug: string;
  locale: "en" | "es";
  limit?: number;
}) =>
  queryOptions({
    queryKey: ["public.resource.page", input] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof getPublicResourcePage>>>,
    staleTime: 5 * 60 * 1000,
  });

export const relatedResourcesQuery = (input: {
  slug: string;
  tags: string[];
  locale: "en" | "es";
  limit?: number;
}) =>
  queryOptions({
    queryKey: ["public.resources.related", input] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof getRelatedResources>>>,
  });

export const publicBlogPostsQuery = () =>
  queryOptions({
    queryKey: ["public.blog.list"] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof listPublicBlogPosts>>>,
    staleTime: 5 * 60 * 1000,
  });

export const publicBlogCategoryQuery = (input: { category: string }) =>
  queryOptions({
    queryKey: ["public.blog.by-category", input] as const,
    queryFn: globalQueryFn as QueryFunction<
      Awaited<ReturnType<typeof listPublicBlogPostsByCategory>>
    >,
    staleTime: 5 * 60 * 1000,
  });

export const publicBlogCategoryCountsQuery = () =>
  queryOptions({
    queryKey: ["public.blog.category-counts"] as const,
    queryFn: globalQueryFn as QueryFunction<
      Awaited<ReturnType<typeof listPublicBlogCategoryCounts>>
    >,
    staleTime: 5 * 60 * 1000,
  });

export const publicBlogPostPageQuery = (input: { slug: string; relatedLimit?: number }) =>
  queryOptions({
    queryKey: ["public.blog.page", input] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof getPublicBlogPostPage>>>,
    staleTime: 5 * 60 * 1000,
  });

export const quoteByIdQuery = (id: string) =>
  queryOptions({
    queryKey: ["public.quote.get", { id }] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof getQuoteById>>>,
  });

export const siteSettingsQuery = () =>
  queryOptions({
    queryKey: ["public.site-settings.get"] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof getSiteSettings>>>,
  });

export const publicEcosystemPartnersQuery = () =>
  queryOptions({
    queryKey: ["public.ecosystem-partners.list"] as const,
    queryFn: globalQueryFn as QueryFunction<
      Awaited<ReturnType<typeof listPublicEcosystemPartners>>
    >,
    staleTime: 5 * 60 * 1000,
  });

// Admin
export const adminProjectsQuery = () =>
  queryOptions({
    queryKey: ["admin.projects.list"] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof listAdminProjects>>>,
    staleTime: 0,
  });

export const adminProjectQuery = (id: string) =>
  queryOptions({
    queryKey: ["admin.project.get", { id }] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof getProject>>>,
    staleTime: 0,
  });

export const adminResourcesQuery = () =>
  queryOptions({
    queryKey: ["admin.resources.list"] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof listAdminResources>>>,
    staleTime: 0,
  });

export const adminResourceQuery = (id: string) =>
  queryOptions({
    queryKey: ["admin.resource.get", { id }] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof getResource>>>,
    staleTime: 0,
  });

export const adminEcosystemPartnersQuery = () =>
  queryOptions({
    queryKey: ["admin.ecosystem-partners.list"] as const,
    queryFn: globalQueryFn as QueryFunction<
      Awaited<ReturnType<typeof listAdminEcosystemPartners>>
    >,
    staleTime: 0,
  });

export const adminBlogPostsQuery = () =>
  queryOptions({
    queryKey: ["admin.blog.list"] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof listAdminBlogPosts>>>,
    staleTime: 0,
  });

export const adminBlogPostQuery = (id: string) =>
  queryOptions({
    queryKey: ["admin.blog.get", { id }] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof getBlogPost>>>,
    staleTime: 0,
  });

export const adminTeamQuery = () =>
  queryOptions({
    queryKey: ["admin.team.list"] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof listTeamMembers>>>,
    staleTime: 0,
  });

export const adminLeadsQuery = () =>
  queryOptions({
    queryKey: ["admin.leads.list"] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof listAuditLeads>>>,
    staleTime: 0,
  });

export const portalOrdersQuery = () =>
  queryOptions({
    queryKey: ["portal.orders.list"] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof listMyOrders>>>,
    staleTime: 0,
  });

export const adminOrdersQuery = () =>
  queryOptions({
    queryKey: ["admin.orders.list"] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof listAllOrders>>>,
    staleTime: 0,
  });

export const adminOrderQuery = (id: string) =>
  queryOptions({
    queryKey: ["admin.order.get", { id }] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof getAdminOrder>>>,
    staleTime: 0,
  });

export const adminAuditSectionsQuery = (order_id: string) =>
  queryOptions({
    queryKey: ["admin.audit-sections.list", { order_id }] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof getAdminAuditSections>>>,
    staleTime: 30 * 1000,
  });

export const adminOrderQueueStatusQuery = (order_id: string) =>
  queryOptions({
    queryKey: ["admin.order.queue-status", { order_id }] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof getOrderQueueStatus>>>,
    staleTime: 0,
    refetchInterval: 10 * 1000,
  });

export const adminReportContentQuery = (order_id: string) =>
  queryOptions({
    queryKey: ["admin.report-content.get", { order_id }] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof getAdminReportContent>>>,
    staleTime: 0,
  });

export const portalOrderSectionsQuery = (order_id: string) =>
  queryOptions({
    queryKey: ["portal.audit-sections.list", { order_id }] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof listMyOrderAuditSections>>>,
    staleTime: 30 * 1000,
  });

export const adminDashboardOrdersQuery = () =>
  queryOptions({
    queryKey: ["admin.orders.list"] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof listAllOrders>>>,
    staleTime: 0,
    refetchInterval: 30 * 1000,
  });

export const adminAuthQuery = () =>
  queryOptions({
    queryKey: ["admin.auth.check"] as const,
    queryFn: globalQueryFn as QueryFunction<Awaited<ReturnType<typeof checkIsAdmin>>>,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

// Convenient invalidation keys for mutations.
export const INVALIDATE = {
  adminProjects: ["admin.projects.list"] as const,
  publicProjects: ["public.projects.list"] as const,
  adminResources: ["admin.resources.list"] as const,
  publicResources: ["public.resources.list"] as const,
  adminEcosystemPartners: ["admin.ecosystem-partners.list"] as const,
  publicEcosystemPartners: ["public.ecosystem-partners.list"] as const,
  adminBlog: ["admin.blog.list"] as const,
  publicBlog: ["public.blog.list"] as const,
  adminTeam: ["admin.team.list"] as const,
  siteSettings: ["public.site-settings.get"] as const,
  adminOrders: ["admin.orders.list"] as const,
  portalOrders: ["portal.orders.list"] as const,
};
